import React, { Component, ErrorInfo, ReactNode } from 'react';
import { sanitizeAndValidateMetaContent, META_LIMITS } from '../utils/metaSanitization';

interface SEOErrorBoundaryProps {
  children: ReactNode;
  fallbackTitle?: string;
  fallbackDescription?: string;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface SEOErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

/**
 * Error boundary component specifically for SEO components
 * Provides fallback SEO meta tags when errors occur and prevents XSS attacks
 */
class SEOErrorBoundary extends Component<SEOErrorBoundaryProps, SEOErrorBoundaryState> {
  constructor(props: SEOErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): SEOErrorBoundaryState {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log the error for monitoring
    console.error('SEOErrorBoundary caught an error:', error, errorInfo);
    
    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  render() {
    if (this.state.hasError) {
      // Render fallback SEO meta tags with sanitized content
      const fallbackTitle = this.props.fallbackTitle || 'AlertaLegal - Legal Publication Alerts';
      const fallbackDescription = this.props.fallbackDescription || 'Monitor legal publications and receive alerts for Costa Rica';

      // Sanitize fallback content to prevent XSS even in error scenarios
      const sanitizedTitle = sanitizeAndValidateMetaContent(fallbackTitle, 'TITLE');
      const sanitizedDescription = sanitizeAndValidateMetaContent(fallbackDescription, 'DESCRIPTION');

      return (
        <>
          <title>{sanitizedTitle}</title>
          <meta name="description" content={sanitizedDescription} />
          <meta name="robots" content="index,follow" />
          <link rel="canonical" href="https://alertalegal.co/" />
          
          {/* Basic Open Graph tags */}
          <meta property="og:title" content={sanitizedTitle} />
          <meta property="og:description" content={sanitizedDescription} />
          <meta property="og:type" content="website" />
          <meta property="og:url" content="https://alertalegal.co/" />
          
          {/* Twitter Card */}
          <meta name="twitter:card" content="summary" />
          <meta name="twitter:title" content={sanitizedTitle} />
          <meta name="twitter:description" content={sanitizedDescription} />
          
          {/* Development error information */}
          {import.meta.env.DEV && (
            <meta name="seo-error" content={`SEO component failed: ${this.state.error?.message || 'Unknown error'}`} />
          )}
        </>
      );
    }

    return this.props.children;
  }
}

export default SEOErrorBoundary;